/**
 * Nicotine River — scroll motion.
 *
 * Three independent behaviours, each of which no-ops cleanly if its markup isn't
 * on the page:
 *
 *   1. reveal   — fade/rise elements as they enter the viewport, once.
 *   2. hero     — a scroll cue that retires itself after the first gesture.
 *   3. counters — the volume band's figures run up to their value, once.
 *
 * The resting state for (1) is injected before first paint by
 * snippets/nr-motion-head.liquid, which also owns the list of reveal targets.
 */
(function () {
  'use strict';

  var root = document.documentElement;

  /* No prefers-reduced-motion check in here any more. Every effect this file
     still touches is declared inside a prefers-reduced-motion: no-preference
     block in nr-motion.css, so the media query decides in one place rather than
     being asked again in JS, where the two could disagree. */

  /* ======================================================================
     1. Reveal on scroll
     ====================================================================== */

  function initReveal() {
    var selector = window.NR_REVEAL;

    // The head snippet bailed (no IntersectionObserver), so nothing was ever
    // hidden and there is nothing to reveal.
    if (!selector || !('IntersectionObserver' in window)) return;

    // Tell the head snippet's failsafe that we made it.
    root.classList.add('nr-motion-ready');

    // Stagger is worked out per observer batch rather than per element up
    // front. Everything that crosses the threshold in the same callback — a row
    // of product cards, the hero's copy on load — cascades together, and a lone
    // element gets no delay. Indexing by sibling position instead would have
    // given every product card a delay of 0, because Prestige wraps each card
    // in its own grid cell.
    var STEP = 45;
    var MAX_DELAY = 180;

    var observer = new IntersectionObserver(function (entries) {
      var arriving = entries.filter(function (entry) {
        return entry.isIntersecting;
      });

      // Callback order is not guaranteed to be document order.
      arriving.sort(function (a, b) {
        return (a.boundingClientRect.top - b.boundingClientRect.top) ||
               (a.boundingClientRect.left - b.boundingClientRect.left);
      });

      arriving.forEach(function (entry, i) {
        var el = entry.target;
        var delay = Math.min(i * STEP, MAX_DELAY);

        // Per spec the transition uses the after-change value of
        // transition-delay, so setting it alongside .nr-in is fine.
        el.style.setProperty('--nr-reveal-delay', delay + 'ms');
        el.classList.add('nr-arming', 'nr-in');
        observer.unobserve(el);

        // Release the compositor layer once the transition has finished.
        window.setTimeout(function () {
          el.classList.remove('nr-arming');
          el.style.removeProperty('--nr-reveal-delay');
        }, delay + 600);
      });
    }, {
      // Positive bottom margin: start the reveal while the element is still
      // ~140px below the fold, so it has already arrived by the time it is
      // actually looked at. The previous -8% did the opposite — it waited until
      // the element was well inside the viewport, so you watched empty space
      // fade in, which is most of what felt slow.
      rootMargin: '0px 0px 140px 0px',
      threshold: 0
    });

    // Deliberately not gated on prefers-reduced-motion. The resting state lives
    // inside a no-preference media query, so under "reduce" these elements are
    // already visible and marking them revealed is a no-op. Skipping the
    // observer instead would strand them: someone who changes the OS setting
    // while the page is open re-arms the hidden state with nothing left to
    // undo it. CSS alone decides whether the reveal animates.
    function arm(scope) {
      if (scope.nodeType !== 1 && scope.nodeType !== 9) return;

      // A late-injected node may itself be a target, not just contain one.
      if (scope.nodeType === 1 && scope.matches && scope.matches(selector)) {
        armOne(scope);
      }

      var found = scope.querySelectorAll(selector);
      for (var i = 0; i < found.length; i++) armOne(found[i]);
    }

    function armOne(el) {
      if (el.classList.contains('nr-reveal')) return;
      el.classList.add('nr-reveal');
      observer.observe(el);
    }

    arm(document);

    // The pre-paint rule in nr-motion-head.liquid hides every target by
    // selector, but only elements this observer knows about are ever revealed.
    // Content that arrives after this point would therefore stay invisible for
    // good — which is exactly what happened on the cart, where EasyLockdown
    // renders the page body client-side and querySelectorAll found nothing at
    // DOMContentLoaded. Watching for insertions closes that gap for any app or
    // theme script that injects or re-renders a region.
    if ('MutationObserver' in window && document.body) {
      var mo = new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) arm(added[j]);
        }
      });

      mo.observe(document.body, { childList: true, subtree: true });

      // Stop watching once the page has settled. Review widgets and chat
      // launchers churn the DOM indefinitely, and there is no reason to keep
      // matching a long selector against every one of their insertions.
      window.setTimeout(function () { mo.disconnect(); }, 10000);
    }

    // Last resort. If anything is still hidden and unmanaged — injected into a
    // detached tree, moved, or added after the observer stopped — show it
    // rather than leave a blank region on the page.
    window.setTimeout(function () {
      var all = document.querySelectorAll(selector);
      for (var i = 0; i < all.length; i++) {
        if (!all[i].classList.contains('nr-in')) {
          all[i].classList.add('nr-reveal', 'nr-in');
        }
      }
    }, 11000);
  }

  /* ======================================================================
     2. Hero scroll cue

     The parallax this used to drive is gone; see the hero comment in
     nr-motion.css for why. What is left is one boolean: has the reader moved.

     No rAF and no measuring. pageYOffset is free to read — it is not a layout
     property — and the attribute is only written when the answer changes, so a
     scroll from the top to the footer costs two style invalidations rather than
     one per frame. Nothing here can force a layout, which is the whole point:
     it is the last thing in the theme still listening for scroll.
     ====================================================================== */

  function initHero() {
    var hero = document.querySelector('.nr-hero');
    if (!hero) return;

    var cue = hero.querySelector('.nr-hero__cue');
    if (!cue) return;

    var scrolled = null;

    function update() {
      var moved = window.pageYOffset > 24;
      if (moved === scrolled) return;
      scrolled = moved;
      cue.setAttribute('data-scrolled', moved ? 'true' : 'false');
    }

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ======================================================================
     Spec ribbon

     Nothing here any more. The drift is a CSS animation in nr-motion.css, on its
     own clock rather than on the reader's scroll, running for as long as the band
     is on the page.

     This file used to compute the position from getBoundingClientRect on every
     frame. The browser now resolves it on the compositor: nothing is measured,
     nothing waits for web fonts to settle before the travel distance is right,
     and no permanent compositor layer is held for the width of the track.
     ====================================================================== */

  /* ======================================================================
     3. Counting figures

     The volume band's numbers run up to their value once, when the band first
     arrives. The point is to say "this is a measured quantity" rather than to
     be an effect, so it is short, it eases out, and it happens once.

     The final text is already in the HTML. This only ever replaces it with a
     smaller number for the length of the animation and then puts the original
     string back, so the figure a reader ends up with is the one the editor
     typed — separators, and anything else it carries, intact. With JavaScript
     off, or under reduced motion, nothing here runs and the figure is simply
     correct from the start.
     ====================================================================== */

  function initCounters() {
    var nodes = document.querySelectorAll('[data-nr-count]');
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window)) return;

    // Asked directly rather than left to CSS: there is no declarative form of
    // "do not run this loop", and a media query cannot stop a setInterval.
    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    if (still && still.matches) return;

    var DURATION = 1100;

    function run(el) {
      var target = parseInt(el.getAttribute('data-nr-count'), 10);
      if (!(target > 0)) return;

      var final = el.textContent;
      var started = null;

      // Same separator style as the value it is counting towards, so the digits
      // do not regroup themselves at the last frame.
      var grouped = final.indexOf(',') !== -1;

      function frame(now) {
        if (started === null) started = now;

        var t = Math.min((now - started) / DURATION, 1);
        // Cubic ease-out: fast enough to read as a measurement being taken,
        // and it settles rather than stopping dead on the total.
        var eased = 1 - Math.pow(1 - t, 3);
        var value = Math.round(target * eased);

        el.textContent = grouped ? value.toLocaleString('en-US') : String(value);

        if (t < 1) {
          window.requestAnimationFrame(frame);
        } else {
          el.textContent = final;
        }
      }

      window.requestAnimationFrame(frame);
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        run(entry.target);
      });
    }, { rootMargin: '0px 0px -15% 0px', threshold: 0 });

    Array.prototype.forEach.call(nodes, function (node) {
      observer.observe(node);
    });
  }

  function init() {
    initReveal();
    initHero();
    initCounters();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
